import {
  Box,
  Button,
  FormControl,
  TextField,
  Typography
} from "@material-ui/core";
import FormError, { useError, useToastError } from "../../components/FormError";
import MaterialTable, { MTableToolbar } from "material-table";
import ReactInputMask from "react-input-mask";
import { useEffect, useRef, useState } from "react";

import ConfirmationDialog from "../../components/ConfirmationDialog";
import Time from "../../lithic/Moment";
import api from "../../api";
import {
  apiCollectionToRowResponse,
  rowQueryToApiParams
} from "../../modules/tableQuery";
import displayPhone from "../../modules/displayPhone";
import tableIcons from "../../components/tableIcons";
import useToggle from "../../state/useToggle";
import UniversalExclusionUpload from "./UniversalExclusionUpload";

const ERROR_MESSAGES = {
  exclusion_already_exists:
    "An exclusion with this email, phone, or SSN already exists.",
  invalid_ssn_format: "Enter a 9-digit SSN (numbers only, no all-same digits).",
  missing_identifiers:
    "Provide at least one of email, phone, or SSN to add an exclusion."
};

const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,253}\.[^\s@]{1,253}$/;

function humanizeExclusionError(error) {
  const key = error?.response?.data?.error?.message;
  return ERROR_MESSAGES[key] || error;
}

export default function UniversalExclusionListPage() {
  const tableRef = useRef();
  const addToggle = useToggle();
  const toastError = useToastError();
  const [formMessage, setFormMessage] = useError();
  const [pendingDelete, setPendingDelete] = useState(null);

  function refreshTable() {
    tableRef.current?.onQueryChange();
  }

  function addExclusion(exclusion) {
    setFormMessage("");
    return api
      .createUniversalExclusion(exclusion)
      .then(() => {
        refreshTable();
        addToggle.turnOff();
      })
      .catch((error) => {
        setFormMessage(humanizeExclusionError(error));
      });
  }

  function deleteExclusion(exclusionId) {
    return api
      .deleteUniversalExclusion(exclusionId)
      .then(refreshTable)
      .catch(toastError);
  }

  const columns = [
    { title: "First Name", field: "firstName", filtering: false },
    { title: "Last Name", field: "lastName", filtering: false },
    { title: "Email", field: "email", filtering: false },
    {
      title: "Phone",
      field: "phone",
      filtering: false,
      render: (row) => displayPhone(row.phone) || row.phone || ""
    },
    {
      title: "SSN (last 4)",
      field: "ssnLastFour",
      filtering: false
    },
    { title: "Source", field: "source", filtering: false },
    {
      title: "Created",
      field: "createdAt",
      filtering: false,
      render: (row) => <Time formatExact="MMM DD, YYYY">{row.createdAt}</Time>
    },
    {
      title: "Actions",
      field: "actions",
      filtering: false,
      render: (row) => (
        <Button
          size="small"
          style={{ backgroundColor: "#f44336", color: "white" }}
          onClick={() =>
            setPendingDelete({
              id: row.id,
              label: row.email || row.phone || `#${row.id}`
            })
          }
        >
          Delete
        </Button>
      )
    }
  ];

  return (
    <>
      <Box style={{ marginTop: 16 }} />
      <MaterialTable
        title="Universal Exclusion List"
        tableRef={tableRef}
        columns={columns}
        icons={tableIcons}
        options={{
          emptyRowsWhenPaging: false,
          filtering: false,
          maxBodyHeight: "calc(100vh - 280px)",
          pageSize: 25,
          pageSizeOptions: [25, 50, 100, 500],
          sorting: false,
          search: true
        }}
        data={(q) =>
          api
            .getUniversalExclusions(rowQueryToApiParams(q))
            .then(apiCollectionToRowResponse)
            .catch((error) => {
              toastError(error);
              return { data: [], page: q.page, totalCount: 0 };
            })
        }
        components={{
          Toolbar: (props) => (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Box style={{ flex: 1 }}>
                <MTableToolbar {...props} />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 8, paddingRight: 16 }}
              >
                <UniversalExclusionUpload onUploadSuccess={refreshTable} />
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={addToggle.turnOn}
                >
                  Add exclusion
                </Button>
              </Box>
            </Box>
          )
        }}
      />

      <AddExclusionModal
        toggle={addToggle}
        onAdd={addExclusion}
        error={formMessage}
        resetError={setFormMessage}
      />

      <ConfirmationDialog
        title="Delete exclusion"
        open={Boolean(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          deleteExclusion(pendingDelete.id);
          setPendingDelete(null);
        }}
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children: "Delete",
          color: "default",
          style: { backgroundColor: "#f44336", color: "white" }
        }}
      >
        <Typography>
          Are you sure you want to delete the exclusion for{" "}
          <strong>{pendingDelete?.label}</strong>? This action cannot be undone.
        </Typography>
      </ConfirmationDialog>
    </>
  );
}

function AddExclusionModal({ toggle, onAdd, error, resetError }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [ssn, setSsn] = useState("");

  useEffect(() => {
    if (toggle.on) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setSsn("");
      resetError("");
    }
  }, [toggle.on]);

  const phoneDigits = phone.replace(/\D/g, "");
  const emailValid = !email || EMAIL_RE.test(email);
  const phoneValid = !phone || phoneDigits.length === 10;
  const hasAny = Boolean(email || phone || ssn);
  const valid = hasAny && emailValid && phoneValid;

  function handleConfirm() {
    const fields = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone: phoneDigits,
      ssn
    };
    const payload = Object.fromEntries(
      Object.entries(fields).filter(([, value]) => value)
    );
    onAdd(payload);
  }

  return (
    <ConfirmationDialog
      title="Add universal exclusion"
      open={toggle.on}
      onCancel={toggle.turnOff}
      onConfirm={handleConfirm}
      cancelProps={{ children: "Cancel" }}
      confirmProps={{ children: "Add", disabled: !valid }}
    >
      <FormError error={error} resetError={resetError} />
      <FormControl margin="normal" fullWidth>
        <TextField
          label="First Name"
          value={firstName}
          variant="outlined"
          onChange={(e) => setFirstName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Last Name"
          value={lastName}
          variant="outlined"
          onChange={(e) => setLastName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Email"
          type="email"
          value={email}
          variant="outlined"
          error={Boolean(email) && !emailValid}
          helperText={
            Boolean(email) && !emailValid ? "Enter a valid email address." : ""
          }
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <ReactInputMask
          mask="(999) 999-9999"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        >
          {(props) => (
            <TextField
              {...props}
              label="Phone"
              type="tel"
              variant="outlined"
              error={Boolean(phone) && !phoneValid}
              helperText={
                Boolean(phone) && !phoneValid ? "Enter a 10-digit phone number." : ""
              }
            />
          )}
        </ReactInputMask>
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="SSN"
          value={ssn}
          variant="outlined"
          inputProps={{ maxLength: 9 }}
          onChange={(e) => setSsn(e.target.value)}
          helperText="Enter 9 digits (no dashes). Required only for SSN-based blocks."
        />
      </FormControl>
    </ConfirmationDialog>
  );
}
